/**
 * CONTACTO E INSCRIPCIONES
 * =============================================================================
 * La fotografía del torneo a pantalla completa, con el texto, la firma y el botón
 * de registro encima. Debajo van el proceso en tres pasos, la ubicación con el
 * mapa, los horarios, las redes y el formulario.
 *
 * -----------------------------------------------------------------------------
 * LOS BOTONES
 * -----------------------------------------------------------------------------
 *   · Regístrate aquí → baja al formulario y pone el foco en el primer campo
 *   · Cada paso del proceso → al formulario, salvo el 2, que lleva a programas
 *   · Ubicación → abre Google Maps
 *   · Instagram, Facebook y TikTok → a los perfiles configurados
 *
 * No hay WhatsApp, como pediste.
 */

import { CONFIG } from '../config';
import { useAjustes } from '../i18n/useAjustes';
import { PantallaCompleta } from '../componentes/PantallaCompleta';
import { Formulario } from '../componentes/Formulario';
import { Icono, IconoEnAro, type NombreIcono } from '../componentes/Iconos';
import { Logo } from '../componentes/Logo';
import { Revelar } from '../componentes/Piezas';
import { useIdioma } from '../i18n/useIdioma';

export function Contacto() {
  const { textos, idioma } = useIdioma();
  const { valor, activo } = useAjustes();
  const t = textos.contacto;

  const irAlFormulario = () => {
    document.getElementById('formulario')?.scrollIntoView({ behavior: 'smooth' });
    /* El foco se pone después de que termine el desplazamiento, para que en el
       teléfono no aparezca el teclado antes de llegar. */
    window.setTimeout(() => {
      document.getElementById('formulario')?.querySelector('input')?.focus();
    }, 620);
  };

  const irA = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const abrirMapa = () => window.open(valor('contacto_mapa'), '_blank', 'noopener');

  /** Las tres redes, con sus perfiles. */
  const redes: Array<{
    nombre: NombreIcono;
    etiqueta: string;
    red: 'instagram' | 'facebook' | 'tiktok';
  }> = [
    { nombre: 'instagram', etiqueta: 'Instagram', red: 'instagram' },
    { nombre: 'facebook', etiqueta: 'Facebook', red: 'facebook' },
    { nombre: 'tiktok', etiqueta: 'TikTok', red: 'tiktok' },
  ];

  /**
   * Los horarios, tal como los puso el administrador en el panel.
   *
   * Se guardan como texto en formato JSON porque son una lista, y una lista no
   * cabe en un campo de texto normal. Va con `try` porque si alguien escribe mal
   * ese texto, la página no puede quedarse en blanco: se enseña el aviso de que
   * están por confirmar.
   */
  const listaHorarios: Array<{ dias: string; horas: string; sede: string }> = (() => {
    try {
      const d = JSON.parse(valor('horarios_lista', '[]'));
      return Array.isArray(d) ? d : [];
    } catch {
      return [];
    }
  })();

  const pasos = t.proceso.map((p, i) => ({
    ...p,
    alPulsar: () => (i === 1 ? irA('programas') : irAlFormulario()),
  }));

  return (
    <>
      <PantallaCompleta
        id="contacto"
        foto={CONFIG.fotos.portadaContacto}
        alt={t.fotoAlt}
        etiqueta={t.etiqueta}
        titulo={<span className="oro">{t.titulo}</span>}
        texto={t.texto}
        encuadre="50% 26%"
        encuadreMovil="50% 20%"
        conFlecha
        textoFlecha={textos.inicio.desliza}
      >
        <p className="firma">
          {t.firma1}
          <br />
          {t.firma2}
        </p>

        <div className="acciones-fila">
          <button type="button" className="boton boton--oro" onClick={irAlFormulario}>
            <span>{t.boton}</span>
            <Icono nombre="flecha" tam={17} />
          </button>
        </div>
      </PantallaCompleta>

      <div className="caja">
        {/* El proceso de inscripción. */}
        <Revelar>
          <p className="antetitulo antetitulo--centrado">{t.procesoEtiqueta}</p>

          <div className="proceso">
            {pasos.map((p) => (
              <button type="button" className="paso" key={p.titulo} onClick={p.alPulsar}>
                <IconoEnAro nombre={p.icono as NombreIcono} tam={54} grosorIcono={22} />
                <span className="paso__titulo">{p.titulo}</span>
                <span className="paso__texto">{p.texto}</span>
              </button>
            ))}
          </div>
        </Revelar>

        {/* La ubicación con el mapa. */}
        <Revelar>
          <div className="ubicacion">
            <div className="ubicacion__datos">
              <p className="ubicacion__rotulo">
                <Icono nombre="pin" tam={15} /> {t.ubicacionTitulo}
              </p>
              <p className="ubicacion__nombre">{valor('contacto_nombre', 'Harborside Park')}</p>
              <p className="ubicacion__direccion">
                {valor('contacto_direccion')}
                <br />
                {valor('contacto_ciudad')}
                <br />
                {valor(idioma === 'en' ? 'contacto_pais_en' : 'contacto_pais_es')}
              </p>
              <button type="button" className="boton boton--marco" onClick={abrirMapa}>
                <span>{t.abrirMapa}</span>
                <Icono nombre="flecha" tam={15} />
              </button>
            </div>

            <div className="ubicacion__mapa">
              {/* El mapa se carga solo cuando ya está a la vista, para no gastar
                  datos de quien abre la página y no baja hasta aquí. */}
              <iframe
                title={t.tituloMapa}
                src={
                  'https://www.google.com/maps?q=' +
                  encodeURIComponent(valor('contacto_direccion') + ', ' + valor('contacto_ciudad')) +
                  '&output=embed'
                }
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </Revelar>

        {/* Los horarios. */}
        <Revelar>
          <div id="horarios" className="horarios">
            <p className="antetitulo">{t.horariosEtiqueta}</p>

            {activo('horarios_confirmados') && listaHorarios.length > 0 ? (
              <div className="proceso">
                {listaHorarios.map((h) => (
                  <div className="paso" key={h.dias + h.horas}>
                    <p className="paso__titulo">{h.dias}</p>
                    <p className="paso__texto">{h.horas}</p>
                    <p className="paso__texto paso__texto--oro">{h.sede}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aviso aviso--aviso">
                <span className="aviso__icono">
                  <Icono nombre="calendario" tam={20} />
                </span>
                <span>
                  {valor(idioma === 'en' ? 'horarios_nota_en' : 'horarios_nota_es')}
                  <div className="acciones-fila">
                    <button type="button" className="boton boton--marco" onClick={irAlFormulario}>
                      {t.botonHorarios}
                    </button>
                  </div>
                </span>
              </div>
            )}
          </div>
        </Revelar>

        {/* Las redes. */}
        <Revelar>
          <div className="redes">
            <p className="redes__titulo">{t.siguenos}</p>

            <div className="redes__lista">
              {redes
                .filter((r) => valor('red_' + r.red))
                .map((r) => (
                  /* Un enlace de verdad, con su direccion: asi se puede abrir en
                     otra pestaña, copiar y ver a donde lleva. */
                  <a
                    className="redes__enlace"
                    key={r.red}
                    href={valor('red_' + r.red)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={r.etiqueta + ' · ' + textos.a11y.enlaceExterno}
                  >
                    <Icono nombre={r.nombre} tam={24} />
                  </a>
                ))}
            </div>

            <div className="redes__logo">
              <Logo alto={40} conTexto />
            </div>
            <p className="redes__lema">{valor('academia_lema', textos.inicio.lema)}</p>
          </div>
        </Revelar>

        {/* El formulario. */}
        <Revelar>
          <Formulario />
        </Revelar>
      </div>
    </>
  );
}
